/**
 * Filling this device's database from a peer.
 *
 * A phone with an empty database can sell nothing, so it has to be filled while
 * there *is* a connection — long before anyone needs it to work without one.
 * Run after sign-in and quietly repeated afterwards, because the menu changes.
 */

import type { AuthenticatedUser } from '@bakery/shared';

import { api } from '../lib/api.js';
import { localCommand, localDbReady } from './local-db.js';

const LAST_RUN_KEY = 'bakery.bootstrap.at';

/** How stale a device's copy may get before it is refreshed. */
const REFRESH_AFTER_MS = 30 * 60 * 1000;

export interface BootstrapOutcome {
  ran: boolean;
  reason?: string;
  rows?: number;
}

/**
 * Never throws, and never blocks anything the user is doing.
 *
 * A device that fails to fill itself is worse off than one that succeeded, but
 * it is not broken — it simply has no offline mode until the next attempt, and
 * saying so quietly beats interrupting a queue at the counter.
 */
export async function bootstrapLocalData(
  user: AuthenticatedUser,
  { force = false }: { force?: boolean } = {},
): Promise<BootstrapOutcome> {
  if (!localDbReady()) return { ran: false, reason: 'no local database on this device' };

  if (!force) {
    const last = Number(localStorage.getItem(LAST_RUN_KEY) ?? 0);
    if (Date.now() - last < REFRESH_AFTER_MS) return { ran: false, reason: 'recently refreshed' };
  }

  try {
    const snapshot = await api.sync.snapshot();
    const applied = await localCommand<{ table: string; applied: number }[]>(
      'sync.apply',
      { tables: snapshot.tables, fromDeviceId: snapshot.fromDeviceId },
      user,
    );

    localStorage.setItem(LAST_RUN_KEY, String(Date.now()));
    return { ran: true, rows: applied.reduce((sum, entry) => sum + entry.applied, 0) };
  } catch (error) {
    return { ran: false, reason: (error as Error).message };
  }
}
